import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Icon } from "@iconify/react";
import { useTheme } from "../../../contexts/ThemeContext";
import { formatTimeAgo } from "../../../utils/date";
import type { BaseMediaThumbnailProps } from "../../media/MediaThumbnail";
import Badge, { BadgeType } from "../../common/Badge";
import HoverPlayButton from "../../common/HoverPlayButton";

type BroadcastStatus = 'live' | 'upcoming' | 'none' | 'completed';

interface MastheadVideoThumbnailProps
  extends Omit<BaseMediaThumbnailProps, "aspectRatio" | "navigationPath"> {
  views?: number;
  size?: "small" | "medium" | "large";
  broadcastStatus?: BroadcastStatus;
  rating?: number;
  category?: string;
}

const MastheadVideoThumbnail: React.FC<MastheadVideoThumbnailProps> = ({
  id,
  title,
  thumbnail,
  createdAt,
  views,
  size = "medium",
  broadcastStatus = 'none',
  rating,
  category,
  className = "",
}) => {
  const { theme } = useTheme();
  const { i18n } = useTranslation();

  const isNew = () => {
    const now = new Date();
    const created = new Date(createdAt);
    const diffHours = (now.getTime() - created.getTime()) / (1000 * 60 * 60);
    return diffHours <= 24;
  };

  const getBadgeType = (): BadgeType | null => {
    switch (broadcastStatus) {
      case 'live':
        return 'live';
      case 'upcoming':
        return 'upcoming';
      case 'none':
      case 'completed':
        if (isNew()) {
          return 'new';
        }
        return null;
      default:
        return null;
    }
  };

  const badgeType = getBadgeType();

  return (
    <Link
      to={`/video/${id}`}
      className={`
        group block w-full
        rounded-2xl overflow-hidden
        transition-all duration-300
        hover:-translate-y-0.5
        hover:shadow-2xl
        ${className}
      `}
    >
      {/* Thumbnail Section */}
      <div className="relative aspect-video">
        <img
          src={thumbnail}
          alt={title}
          className="
            w-full h-full 
            object-cover
            transition-transform duration-500
            group-hover:scale-[1.03]
          "
          loading="lazy"
        />

        {badgeType && (
          <div className="absolute top-3 start-3 z-10">
            <Badge type={badgeType} />
          </div>
        )}

        {/* Hover Overlay */}
        <div
          className="
            absolute inset-0
            bg-gradient-to-t from-surface-primary/5 to-surface-primary/10
            opacity-0 group-hover:opacity-100
            transition-opacity duration-500
          "
        />

        <HoverPlayButton icon="ph:play-fill" />
      </div>

      {/* Content Section */}
      <div
        style={{
          backgroundColor:
            theme.mode === "dark"
              ? "var(--colors-surface-secondary)"
              : "rgba(var(--colors-background-primary-rgb), 0.06)",
          borderTop: `1px solid rgba(var(${theme.mode === "dark" ? "--colors-text-primary" : "--colors-text-inverse"}), 0.1)`
        }}
        className="px-7 py-5 border-t"
      >
        <div className="min-h-[56px] mb-2.5">
          <h3
            className={`
              text-lg font-semibold
              ${theme.mode === "dark" ? "text-text-primary" : "text-text-inverse"}
              leading-snug
              line-clamp-2
              transition-colors duration-300
              group-hover:text-primary-500
            `}
          >
            {title}
          </h3>
        </div>

        <div
          className={`
            flex items-center gap-4
            text-sm font-medium
            opacity-50
            ${theme.mode === "dark" ? "text-text-primary" : "text-text-inverse"}
          `}
        >
          {views && (
            <>
              <div className="flex items-center gap-1.5">
                <Icon icon="ph:eye-thin" className="w-4 h-4" />
                <span>{views.toLocaleString()}</span>
              </div>
              <span className="text-text-secondary/30">•</span>
            </>
          )}
          <div className="flex items-center gap-1.5">
            <Icon icon="ph:clock-thin" className="w-4 h-4" />
            <time dateTime={createdAt.toString()}>
              {formatTimeAgo(createdAt, i18n.language)}
            </time>
          </div>
        </div>
      </div>
    </Link>
  );
};

export default MastheadVideoThumbnail;
